import type { FC, ReactNode } from 'react'
import type { ProjectCard } from '@/lib/projects'

import { useRef } from 'react'
import { ArrowUpRight } from 'lucide-react'
import { cn, cssVars } from '@/lib/utils'
import CropMarks from '@/components/generals/crop-marks'
import RollText from '@/components/generals/roll-text'
import SectionLabel from '@/components/generals/section-label'
import TransitionLink from '@/components/motion/transition-link'
import ParallaxImage from '@/components/motion/parallax-image'
import { gsap, MOTION_QUERIES, useGSAP } from '@/lib/motion'

interface SelectedWorkProps {
    projects: ProjectCard[]
    total: number
}

interface ProjectFactsProps {
    project: ProjectCard
    compact?: boolean
}

// Sector, summary and the two headline results of a project, all editable in
// place: every value carries its own data-mdlfy-cms tag.
const ProjectFacts: FC<ProjectFactsProps> = ({ project, compact = false }): ReactNode => (
    <>
        <p className='type-label text-quiet'>
            <span data-mdlfy-cms={`projects:${project.id}:sector`}>{project.sector}</span>
        </p>

        <h3 className={cn('mt-4 break-words', compact ? 'font-heading text-[clamp(2.2rem,3.7vw,4.2rem)] font-[650] leading-[0.95] tracking-[-0.04em]' : 'type-h1')}>
            <span data-mdlfy-cms={`projects:${project.id}:name`}>{project.name}</span>
        </h3>

        {project.summary && (
            <p className='mt-6 max-w-[42ch] text-quiet' data-mdlfy-cms={`projects:${project.id}:summary`}>
                {project.summary}
            </p>
        )}

        <dl className='mt-8 grid max-w-md grid-cols-2 gap-6 border-t border-rule pt-6'>
            {project.stat_1_value && (
                <div>
                    <dt className='type-label text-quiet'>
                        <span data-mdlfy-cms={`projects:${project.id}:stat_1_label`}>{project.stat_1_label}</span>
                    </dt>

                    <dd className={cn('mt-2 tabular-nums', compact ? 'type-h3' : 'type-h2')}>
                        <span data-mdlfy-cms={`projects:${project.id}:stat_1_value`}>{project.stat_1_value}</span>
                    </dd>
                </div>
            )}

            {project.stat_2_value && (
                <div>
                    <dt className='type-label text-quiet'>
                        <span data-mdlfy-cms={`projects:${project.id}:stat_2_label`}>{project.stat_2_label}</span>
                    </dt>

                    <dd className={cn('mt-2 tabular-nums', compact ? 'type-h3' : 'type-h2')}>
                        <span data-mdlfy-cms={`projects:${project.id}:stat_2_value`}>{project.stat_2_value}</span>
                    </dd>
                </div>
            )}
        </dl>
    </>
)

// Featured case studies as a sticky stack: each new project's screen wipes up
// over the last while the facts beside it hand over, like turning pages.
const SelectedWork: FC<SelectedWorkProps> = ({ projects = [], total = 0 }): ReactNode => {
    const sectionRef = useRef<HTMLElement>(null)
    const count = projects.length

    useGSAP(() => {
        const section = sectionRef.current

        if (!section || count < 2) return

        const q = gsap.utils.selector(section)
        const mm = gsap.matchMedia()

        mm.add(MOTION_QUERIES.desktop, () => {
            const cards = q('[data-stack-card]')
            const images = q('[data-stack-image]')
            const shades = q('[data-stack-shade]')
            const panels = q('[data-stack-panel]')

            gsap.set(cards.slice(1), { clipPath: 'inset(100% 0% 0% 0%)' })
            gsap.set(panels.slice(1), { autoAlpha: 0, yPercent: 12 })

            const timeline = gsap.timeline({
                defaults: { ease: 'power2.inOut' },
                scrollTrigger: {
                    trigger: section,
                    start: 'top top',
                    end: 'bottom bottom',
                    scrub: 0.7
                }
            })

            for (let index = 1; index < count; index++) {
                const at = index - 1

                timeline
                    .to(cards[index], { clipPath: 'inset(0% 0% 0% 0%)', duration: 1 }, at)
                    .fromTo(images[index], { scale: 1.3 }, { scale: 1, duration: 1 }, at)
                    .to(images[index - 1], { scale: 0.92, duration: 1 }, at)
                    .to(shades[index - 1], { opacity: 0.55, duration: 1 }, at)
                    .to(panels[index - 1], { autoAlpha: 0, yPercent: -12, duration: 0.4 }, at + 0.1)
                    .to(panels[index], { autoAlpha: 1, yPercent: 0, duration: 0.4 }, at + 0.55)
                    .to(q('[data-stack-digits]'), { yPercent: -100 * index, duration: 0.6 }, at + 0.3)
            }

            timeline.to({}, { duration: 0.35 })
        })

        return () => mm.revert()
    }, { scope: sectionRef, dependencies: [count] })

    if (!count) return null

    return (
        <section
            id='work'
            ref={sectionRef}
            data-scene='paper'
            aria-labelledby='work-title'
            className='relative motion-safe:lg:h-[var(--stack-height)]'
            style={cssVars({ '--stack-height': `${count * 100 + 40}vh` })}
        >
            <div className='hidden h-svh grid-cols-12 gap-8 px-8 pb-10 pt-28 motion-safe:lg:sticky motion-safe:lg:top-0 motion-safe:lg:grid'>
                <div className='col-span-4 flex flex-col'>
                    <SectionLabel index='04'>
                        <span id='work-title'>
                            Selected work
                        </span>
                    </SectionLabel>

                    <div className='relative mt-10 flex-1'>
                        {projects.map(project => (
                            <article key={project.id} data-stack-panel='' className='absolute inset-x-0 top-0'>
                                <ProjectFacts project={project} compact />

                                <TransitionLink href={`/work/${project.slug}`} className='group mt-8 inline-flex items-center gap-2 font-medium'>
                                    <RollText>
                                        View case study
                                    </RollText>

                                    <ArrowUpRight aria-hidden='true' className='size-4' />
                                </TransitionLink>
                            </article>
                        ))}
                    </div>

                    <div className='flex items-end justify-between border-t border-rule pt-5'>
                        <p className='type-label flex items-center gap-2 text-quiet'>
                            <span className='inline-block h-[1.4em] overflow-hidden text-fore'>
                                <span data-stack-digits='' className='flex flex-col'>
                                    {projects.map((project, index) => (
                                        <span key={project.id} className='h-[1.4em] tabular-nums'>
                                            0{index + 1}
                                        </span>
                                    ))}
                                </span>
                            </span>
                            <span>
                                / 0{count}
                            </span>
                        </p>

                        <TransitionLink href='/work' className='group inline-flex items-center gap-2 text-sm font-medium'>
                            <RollText>
                                {`All ${total} projects`}
                            </RollText>

                            <ArrowUpRight aria-hidden='true' className='size-4' />
                        </TransitionLink>
                    </div>
                </div>

                <div className='relative col-span-8'>
                    {projects.map((project, index) => (
                        <TransitionLink
                            key={project.id}
                            href={`/work/${project.slug}`}
                            data-stack-card=''
                            data-cursor='View case'
                            aria-label={`${project.name} case study`}
                            className='absolute inset-0 block overflow-hidden bg-ink'
                            style={{ zIndex: index + 1 }}
                        >
                            <img
                                data-stack-image=''
                                src={project.hero_image ?? project.cover_image ?? ''}
                                alt={project.hero_image_alt ?? ''}
                                loading='lazy'
                                decoding='async'
                                className='absolute inset-0 size-full object-cover'
                            />

                            <span data-stack-shade='' aria-hidden='true' className='absolute inset-0 bg-ink opacity-0' />
                        </TransitionLink>
                    ))}

                    <CropMarks className='z-[20] text-fore' />
                </div>
            </div>

            <div className='px-5 py-24 md:px-8 motion-safe:lg:hidden'>
                <div className='flex items-end justify-between gap-6'>
                    <SectionLabel index='04'>
                        Selected work
                    </SectionLabel>

                    <TransitionLink href='/work' className='type-label link-line'>
                        {`All ${total} projects`}
                    </TransitionLink>
                </div>

                <div className='mt-12 space-y-20'>
                    {projects.map(project => (
                        <article key={project.id}>
                            <TransitionLink href={`/work/${project.slug}`} aria-label={`${project.name} case study`} className='relative block'>
                                <ParallaxImage
                                    src={project.hero_image ?? project.cover_image ?? ''}
                                    alt={project.hero_image_alt ?? ''}
                                    className='aspect-[16/10]'
                                    amount={10}
                                />
                            </TransitionLink>

                            <div className='mt-8'>
                                <ProjectFacts project={project} />

                                <TransitionLink href={`/work/${project.slug}`} className='group mt-6 inline-flex items-center gap-2 font-medium'>
                                    <RollText>
                                        View case study
                                    </RollText>

                                    <ArrowUpRight aria-hidden='true' className='size-4' />
                                </TransitionLink>
                            </div>
                        </article>
                    ))}
                </div>
            </div>
        </section>
    )
}

export default SelectedWork
